import { isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import * as React from 'react';
import { Group as AriaGroup } from 'react-aria-components';
import PhoneInput from 'react-phone-number-input/input';

import { InfoCircleIcon } from '../../icons';
import { cx } from '../../utils/cx';

import HintText from './HintText';
import { TextField } from './Input';
import Label from './Label';

// Styled input component compatible with react-phone-number-input
const StyledPhoneInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, disabled, ...props }, ref) => {
    return (
      <input
        {...props}
        ref={ref}
        type="tel"
        disabled={disabled}
        className={cx(
          'text-md text-primary outline-hidden placeholder:text-placeholder autofill:text-primary m-0 w-full bg-transparent px-3.5 py-2.5 ring-0 autofill:rounded-lg',
          disabled && 'text-disabled cursor-not-allowed',
          className
        )}
      />
    );
  }
);
StyledPhoneInput.displayName = 'StyledPhoneInput';

interface IPhoneNumberInputProps {
  /** Current phone number value */
  value: string;
  /** Callback when the phone number changes */
  onChange: (value: string) => void;
  /** Label text for the input */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Helper text displayed when there's no error */
  hint?: string;
  /** Whether the field is required */
  isRequired?: boolean;
  /** Whether the input is disabled */
  isDisabled?: boolean;
  /** Custom class name */
  className?: string;
  /** External validation result - when provided, skips internal validation */
  validation?: IPhoneValidationResult;
}

export interface IPhoneValidationResult {
  isValid: boolean;
  errorMessage: string;
  e164Value: string | null;
}

/**
 * Validates a phone number and returns validation result
 */
export const validatePhoneNumber = (
  phoneNumber: string,
  isRequired: boolean,
  hasTouched: boolean
): IPhoneValidationResult => {
  if (!phoneNumber) {
    if (isRequired && hasTouched) {
      return { isValid: false, errorMessage: 'Phone number is required', e164Value: null };
    }
    return { isValid: !isRequired, errorMessage: '', e164Value: null };
  }

  const digitsOnly = phoneNumber.replace(/\D/g, '');
  if (digitsOnly.length > 0 && !isValidPhoneNumber(phoneNumber, 'US')) {
    return {
      isValid: false,
      errorMessage: 'Please enter a valid US phone number',
      e164Value: null,
    };
  }

  try {
    const parsed = parsePhoneNumberWithError(phoneNumber, 'US');
    return { isValid: true, errorMessage: '', e164Value: parsed?.format('E.164') || phoneNumber };
  } catch {
    return {
      isValid: false,
      errorMessage: 'Please enter a valid US phone number',
      e164Value: null,
    };
  }
};

/**
 * Hook to manage phone number input state and validation
 */
export const usePhoneNumberInput = (initialValue: string, isRequired: boolean = false) => {
  const [value, setValue] = React.useState(initialValue);
  const [hasTouched, setHasTouched] = React.useState(false);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const validation = React.useMemo(
    () => validatePhoneNumber(value, isRequired, hasTouched),
    [value, isRequired, hasTouched]
  );

  const handleChange = React.useCallback((newValue: string) => {
    setHasTouched(true);
    setValue(newValue);
  }, []);

  return {
    value,
    setValue,
    hasTouched,
    setHasTouched,
    validation,
    handleChange,
  };
};

const PhoneNumberInput = ({
  value,
  onChange,
  label = 'Phone number',
  placeholder = '(555) 555-5555',
  hint,
  isRequired = false,
  isDisabled = false,
  className,
  validation: externalValidation,
}: IPhoneNumberInputProps) => {
  // Only track touched state internally if no external validation is provided
  const [internalHasTouched, setInternalHasTouched] = React.useState(false);

  const internalValidation = React.useMemo(
    () => validatePhoneNumber(value, isRequired, internalHasTouched),
    [value, isRequired, internalHasTouched]
  );

  // Use external validation if provided, otherwise fall back to internal
  const { errorMessage } = externalValidation ?? internalValidation;
  const isInvalid = !!errorMessage;

  return (
    <TextField isInvalid={isInvalid} className={cx('w-full', className)}>
      {label && <Label isRequired={isRequired}>{label}</Label>}
      <AriaGroup
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        className={({ isFocusWithin, isInvalid: groupInvalid, isDisabled: groupDisabled }) =>
          cx(
            'bg-primary shadow-xs ring-primary relative flex w-full flex-row place-content-center place-items-center rounded-lg ring-1 ring-inset transition-shadow duration-100 ease-linear',
            isFocusWithin && !groupDisabled && 'ring-brand ring-2',
            groupInvalid && 'ring-error_subtle',
            groupInvalid && isFocusWithin && !groupDisabled && 'ring-error ring-2',
            groupDisabled && 'bg-disabled_subtle ring-disabled cursor-not-allowed'
          )
        }
      >
        <PhoneInput
          country="US"
          value={value}
          onChange={(val) => {
            if (!externalValidation) {
              setInternalHasTouched(true);
            }
            onChange(val || '');
          }}
          disabled={isDisabled}
          inputComponent={StyledPhoneInput}
          placeholder={placeholder}
          className="w-full"
        />
        {isInvalid && (
          <InfoCircleIcon className="text-fg-error-secondary pointer-events-none absolute right-3.5 size-4" />
        )}
      </AriaGroup>
      {errorMessage && <HintText isInvalid>{errorMessage}</HintText>}
      {!errorMessage && hint && <HintText>{hint}</HintText>}
    </TextField>
  );
};

export default PhoneNumberInput;
